/**
 * The "turn on desktop alerts" prompt: the words and the small decisions around it, kept import-free
 * so cypress/node-tests/notification-prompt-copy.test.js can run this exact file.
 *
 * Two places draw the prompt, from this one source:
 *   - the classic header's pages: full-width cards in Layout.vue;
 *   - the app shell: one row inside the bell's popover (AppNotificationsPopover.vue) plus a dot on the
 *     bell (AppTopBar.vue), so no card pushes the page down (2026-10-13).
 * Layout.vue owns the state (showNotificationBanner / showSoundTestBanner / soundTestResult) and the
 * actions (Enable, Not now, Test again, Done); this file only turns that state into copy.
 */

/** Result of detectSiteAudioMuted(): 'audible' | 'muted' | 'unknown'. */
export function soundTestCopy(result) {
  if (result === 'muted') {
    return {
      variant: 'error',
      icon: 'fa-volume-off',
      title: 'Sound appears to be muted for this site',
      body: 'Click the lock icon next to the URL, turn on <strong>Sound</strong>, then reload this page.',
    };
  }
  if (result === 'audible') {
    return {
      variant: 'success',
      icon: 'fa-volume-up',
      title: 'Sound is working',
      body: 'You should have just heard a short chime. You’re all set for chat &amp; SMS alerts.',
    };
  }
  return {
    variant: 'info',
    icon: 'fa-volume-up',
    title: 'Did you hear that?',
    body: 'You should have just heard a short test sound. If not, click the lock icon next to the URL and make sure <strong>Sound</strong> is allowed, then reload.',
  };
}

/**
 * What the app shell is told. `phase` is 'ask' (alerts not turned on yet), 'sound' (the test sound
 * just played), or '' (nothing to show). The sound check wins when both are on, because it follows an
 * Enable click.
 */
export function alertsPromptFor({ showAsk, showSound, soundResult }) {
  if (showSound) return { phase: 'sound', result: soundResult || 'unknown' };
  if (showAsk) return { phase: 'ask', result: '' };
  return { phase: '', result: '' };
}

/**
 * The words on the chip beside the bell (desktop). '' means nothing to flag: alerts still to be
 * turned on, or sound found muted (the one outcome the person has to act on). A working or unknown
 * sound check flags nothing.
 */
export function alertsChipLabel(prompt) {
  if (!prompt) return '';
  if (prompt.phase === 'ask') return 'Turn on alerts';
  if (prompt.phase === 'sound' && prompt.result === 'muted') return 'Sound is muted';
  return '';
}

/** Whether the bell flags anything: the chip on desktop, the larger amber dot on tablet / phone. */
export function bellNeedsAttention(prompt) {
  return alertsChipLabel(prompt) !== '';
}
